import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Boxes,
  CloudDownload,
  DollarSign,
  Download,
  FileText,
  FileUp,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from 'lucide-react';
import {
  FISCAL_DOCUMENT_KIND_LABELS,
  FISCAL_DOCUMENT_KINDS,
  FISCAL_DOCUMENT_ORIGIN_LABELS,
  FISCAL_SITUATION_LABELS,
  FISCAL_STATUS_LABELS,
  FISCAL_TARGET_STATUS_LABELS,
  XML_ONLY_KINDS,
  formatCents,
  formatDocument,
  type FiscalDocument,
  type FiscalDocumentSummary,
  type Paginated,
  type SefazSyncResult,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SummaryCards } from '@/components/SummaryCards';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useSuppliers, todayISO } from '@/lib/queries';
import { SITUATION_TONE, documentoPath } from './documentos-shared';
import { DateInput } from '@/components/DateInput';
import { ConciliacaoFinanceiraModal, ConciliacaoItensModal } from './NotaConciliacao';

type CampoDeData = 'entrada' | 'emissao' | 'cadastro';

const CAMPOS_DE_DATA: Array<{ value: CampoDeData; label: string }> = [
  { value: 'entrada', label: 'Data de entrada' },
  { value: 'emissao', label: 'Data de emissão' },
  { value: 'cadastro', label: 'Data de cadastro' },
];

/** O período do mesmo tamanho, logo antes — para o "vs. período anterior". */
function periodoAnterior(from: string, to: string): { from: string; to: string } | null {
  if (!from || !to) return null;
  const inicio = new Date(`${from}T00:00:00.000Z`);
  const fim = new Date(`${to}T00:00:00.000Z`);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime()) || fim < inicio) return null;
  const dias = Math.round((fim.getTime() - inicio.getTime()) / 86_400_000) + 1;
  const antesFim = new Date(inicio);
  antesFim.setUTCDate(antesFim.getUTCDate() - 1);
  const antesInicio = new Date(antesFim);
  antesInicio.setUTCDate(antesInicio.getUTCDate() - (dias - 1));
  return { from: antesInicio.toISOString().slice(0, 10), to: antesFim.toISOString().slice(0, 10) };
}

/** "+12% vs. período anterior" — ou nada, quando não há base para comparar. */
function variacao(atual: number, anterior: number | undefined): string | undefined {
  if (anterior === undefined || anterior === 0) return undefined;
  const pct = Math.round(((atual - anterior) / anterior) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}% vs. período anterior`;
}

/** Conciliação de um destino: Sim, Não, ou traço quando não se aplica. */
function Conciliado({ status }: { status: FiscalDocument['financialStatus'] }) {
  if (status === 'LANCADO' || status === 'COBERTO') return <StatusBadge kind="ok">Sim</StatusBadge>;
  if (status === 'PENDENTE') return <StatusBadge kind="bad">Não</StatusBadge>;
  return (
    <span className="muted" title={FISCAL_TARGET_STATUS_LABELS[status]}>
      —
    </span>
  );
}

/**
 * Notas de entrada (Compras).
 *
 * O que entrou, de quem, e se já está conciliado no Financeiro (virou conta a
 * pagar) e no Estoque (os itens deram entrada). Cada linha tem as ações da
 * conciliação à mão: o $ abre a conciliação financeira, a caixa a dos itens.
 *
 * A nota NÃO dá entrada de estoque quando existe pedido ou recebimento
 * vinculado: a quantidade física verdadeira é a ACEITA na portaria.
 */
export function DocumentosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [dateField, setDateField] = useState<CampoDeData>('entrada');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [numero, setNumero] = useState('');
  const [personId, setPersonId] = useState('');
  const [financeiro, setFinanceiro] = useState('');
  const [estoque, setEstoque] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [importing, setImporting] = useState(false);
  const [manual, setManual] = useState(false);
  const [financeiroDe, setFinanceiroDe] = useState<FiscalDocument | null>(null);
  const [itensDe, setItensDe] = useState<FiscalDocument | null>(null);
  const [excluindo, setExcluindo] = useState<FiscalDocument | null>(null);

  const suppliers = useSuppliers();

  const filtros = {
    search: numero || undefined,
    personId: personId || undefined,
    kind: kind || undefined,
    financeiro: financeiro || undefined,
    estoque: estoque || undefined,
    from: from || undefined,
    to: to || undefined,
    dateField,
  };

  const listQuery = useQuery({
    queryKey: ['fiscal-documents', filtros, page, perPage],
    queryFn: () =>
      api.get<Paginated<FiscalDocument>>('/fiscal-documents', { ...filtros, page, perPage }),
  });

  const summaryQuery = useQuery({
    queryKey: ['fiscal-documents', 'summary', filtros],
    queryFn: () => api.get<FiscalDocumentSummary>('/fiscal-documents/summary', filtros),
  });

  // O mesmo recorte, no período de mesmo tamanho logo antes.
  const anterior = periodoAnterior(from, to);
  const anteriorQuery = useQuery({
    queryKey: ['fiscal-documents', 'summary', 'anterior', filtros, anterior],
    queryFn: () =>
      api.get<FiscalDocumentSummary>('/fiscal-documents/summary', { ...filtros, ...anterior }),
    enabled: Boolean(anterior),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
    void queryClient.invalidateQueries({ queryKey: ['fiscal-document'] });
    void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
  };

  /**
   * Busca na SEFAZ — integração REAL, pelo certificado A1 (ADR-017).
   *
   * Se a SEFAZ não responder, o erro aparece como erro. Nunca se mostra
   * "consulta concluída" para uma consulta que falhou.
   */
  const syncMutation = useMutation({
    mutationFn: () => api.post<SefazSyncResult>('/fiscal-documents/sync-sefaz', { uf: 'AC' }),
    onSuccess: refresh,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/fiscal-documents/${id}`),
    onSuccess: () => {
      setExcluindo(null);
      refresh();
    },
  });

  const resumo = summaryQuery.data;
  const abas = resumo?.byKind ?? [];
  const totalDeDocumentos = abas.reduce((soma, aba) => soma + aba.count, 0);
  const antes = anteriorQuery.data;
  const temFiltro = Boolean(numero || personId || financeiro || estoque || kind || from || to);

  const limparTudo = () => {
    setFrom('');
    setTo('');
    setNumero('');
    setPersonId('');
    setFinanceiro('');
    setEstoque('');
    setKind('');
    setPage(1);
  };
  const muda = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const cards = [
    {
      key: 'entradas',
      label: 'Total de entradas',
      value: String(resumo?.documents ?? 0),
      hint: variacao(resumo?.documents ?? 0, antes?.documents) ?? 'Notas no filtro',
      tone: 'neutro' as const,
    },
    {
      key: 'valor',
      label: 'Valor total das notas',
      value: formatCents(resumo?.totalCents ?? 0),
      hint:
        variacao(resumo?.totalCents ?? 0, antes?.totalCents) ??
        ((resumo?.cancelled ?? 0) > 0
          ? `${resumo?.cancelled} cancelada(s) fora do total`
          : 'Sem canceladas nem ignoradas'),
      tone: 'neutro' as const,
    },
    {
      key: 'pendente-financeiro',
      label: 'Pendente conciliação financeira',
      value: formatCents(resumo?.pendingFinancialCents ?? 0),
      hint: `${resumo?.pendingFinancialCount ?? 0} nota(s) pendente(s)`,
      tone: (resumo?.pendingFinancialCount ?? 0) > 0 ? ('atencao' as const) : ('neutro' as const),
      active: financeiro === 'nao',
      onClick: () => muda(setFinanceiro)(financeiro === 'nao' ? '' : 'nao'),
    },
    {
      key: 'pendente-estoque',
      label: 'Pendente conciliação de estoque',
      value: formatCents(resumo?.pendingStockCents ?? 0),
      hint: `${resumo?.pendingStockCount ?? 0} nota(s) pendente(s)`,
      tone: (resumo?.pendingStockCount ?? 0) > 0 ? ('ruim' as const) : ('neutro' as const),
      active: estoque === 'nao',
      onClick: () => muda(setEstoque)(estoque === 'nao' ? '' : 'nao'),
    },
  ];

  /** Ícone de ação da linha. O clique não abre a nota — faz só a ação. */
  const acao = (
    label: string,
    Icon: typeof Pencil,
    onClick: () => void,
    opts: { danger?: boolean; disabled?: boolean } = {},
  ) => (
    <button
      type="button"
      className={`nf-acao${opts.danger ? ' nf-acao--perigo' : ''}`}
      aria-label={label}
      title={label}
      disabled={opts.disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Icon size={15} aria-hidden />
    </button>
  );

  const columns: Array<Column<FiscalDocument>> = [
    {
      key: 'number',
      label: 'Número',
      strong: true,
      mono: true,
      width: 96,
      render: (row) => (
        <span title={`${FISCAL_DOCUMENT_KIND_LABELS[row.kind]} · ${row.code}`}>
          {row.number}
          {row.series ? `/${row.series}` : ''}
        </span>
      ),
    },
    {
      key: 'supplier',
      label: 'Fornecedor',
      render: (row) => row.supplierTradeName ?? row.supplierName,
    },
    {
      key: 'document',
      label: 'CNPJ/CPF',
      mono: true,
      width: 140,
      render: (row) => (row.supplierDocument ? formatDocument(row.supplierDocument) : '—'),
    },
    {
      key: 'totalCents',
      label: 'Valor total',
      right: true,
      mono: true,
      width: 112,
      render: (row) => formatCents(row.totalCents),
    },
    {
      key: 'entryDate',
      label: 'Entrada',
      mono: true,
      width: 92,
      render: (row) => (row.entryDate ? formatDate(row.entryDate) : '—'),
    },
    {
      key: 'issueDate',
      label: 'Emissão',
      mono: true,
      width: 92,
      render: (row) => formatDate(row.issueDate),
    },
    {
      key: 'createdAt',
      label: 'Cadastro',
      mono: true,
      width: 92,
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'fin',
      label: 'Conc. Fin.',
      width: 84,
      render: (row) => <Conciliado status={row.financialStatus} />,
    },
    {
      key: 'est',
      label: 'Conc. Estoq.',
      width: 92,
      render: (row) => <Conciliado status={row.stockStatus} />,
    },
    {
      key: 'situation',
      label: 'Status',
      width: 150,
      render: (row) => (
        <StatusBadge kind={SITUATION_TONE[row.situation]}>
          {FISCAL_SITUATION_LABELS[row.situation]}
        </StatusBadge>
      ),
    },
    {
      key: 'acoes',
      label: 'Ações',
      width: 160,
      render: (row) => (
        <div className="nf-acoes">
          {acao('Abrir e editar a nota', Pencil, () =>
            void navigate({ to: documentoPath(row.id) }),
          )}
          {can('documentos-fiscais:lancar') &&
            acao('Conciliação financeira', DollarSign, () => setFinanceiroDe(row))}
          {can('documentos-fiscais:lancar') &&
            acao('Conciliação de itens e estoque', Boxes, () => setItensDe(row))}
          {can('documentos-fiscais:excluir') &&
            acao('Excluir a nota', Trash2, () => setExcluindo(row), {
              danger: true,
              // Nota que já virou conta ou estoque não se exclui: o lançamento
              // ficaria sem origem. Cancele ou estorne lá primeiro.
              disabled: row.financialStatus === 'LANCADO' || row.stockStatus === 'LANCADO',
            })}
        </div>
      ),
    },
  ];

  const exportar = () => {
    const linhas = (listQuery.data?.data ?? []).map((row) =>
      [
        row.number + (row.series ? `/${row.series}` : ''),
        row.supplierName,
        row.supplierDocument ?? '',
        (row.totalCents / 100).toFixed(2).replace('.', ','),
        row.entryDate ? formatDate(row.entryDate) : '',
        formatDate(row.issueDate),
        formatDate(row.createdAt),
        FISCAL_TARGET_STATUS_LABELS[row.financialStatus],
        FISCAL_TARGET_STATUS_LABELS[row.stockStatus],
        FISCAL_SITUATION_LABELS[row.situation],
        FISCAL_DOCUMENT_KIND_LABELS[row.kind],
        FISCAL_DOCUMENT_ORIGIN_LABELS[row.origin],
        FISCAL_STATUS_LABELS[row.fiscalStatus],
        row.accessKey ?? '',
      ].join(';'),
    );
    const csv = [
      'Número;Fornecedor;CNPJ/CPF;Valor total;Entrada;Emissão;Cadastro;Conc. financeira;Conc. estoque;Status;Tipo;Origem;SEFAZ;Chave de acesso',
      ...linhas,
    ].join('\n');
    // BOM para o Excel brasileiro abrir com acento certo.
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notas-de-entrada-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <WorkspaceHeader
        title="Notas de entrada"
        description="Gerencie as notas fiscais de entrada e acompanhe as conciliações financeira e de estoque."
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="outline"
              icon={Download}
              onClick={exportar}
              disabled={!listQuery.data?.data.length}
            >
              Exportar
            </Button>
            {can('documentos-fiscais:criar') && (
              <>
                <Button
                  variant="outline"
                  icon={CloudDownload}
                  loading={syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  Buscar na SEFAZ
                </Button>
                <Button variant="outline" icon={FileUp} onClick={() => setImporting(true)}>
                  Importar NF-e
                </Button>
                <Button variant="primary" icon={Plus} onClick={() => setManual(true)}>
                  Nova entrada
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="workspace__body">
        {syncMutation.isError && (
          <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
            {errorMessage(syncMutation.error)}
          </div>
        )}
        {syncMutation.isSuccess && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              padding: '10px 14px',
              borderRadius: 10,
              border: `1px solid ${T.mareLight}`,
              background: T.mareTint,
              fontSize: 12.5,
            }}
          >
            {syncMutation.data.message}
            {syncMutation.data.hasMore ? ' Ainda há lote na fila — busque de novo.' : ''}
          </div>
        )}

        {/* ------------------------------------------------------- filtros */}
        <section className="nf-filtros" aria-label="Filtros das notas">
          <div className="nf-filtros__periodo">
            <span className="nf-filtros__rotulo">Período por</span>
            <div className="nf-pilulas" role="radiogroup" aria-label="Período por">
              {CAMPOS_DE_DATA.map((campo) => (
                <label
                  key={campo.value}
                  className={`nf-pilula${dateField === campo.value ? ' is-active' : ''}`}
                >
                  <input
                    type="radio"
                    name="nf-campo-data"
                    value={campo.value}
                    checked={dateField === campo.value}
                    onChange={() => {
                      setDateField(campo.value);
                      setPage(1);
                    }}
                  />
                  {campo.label}
                </label>
              ))}
            </div>
          </div>

          <div className="nf-filtros__grade">
            <div className="field">
              <span className="field__label">Período</span>
              <div className="nf-periodo">
                <DateInput
                  className="input input--mono"
                  value={from}
                  aria-label="Período — de"
                  onChange={(iso) => muda(setFrom)(iso)}
                />
                <span aria-hidden>→</span>
                <DateInput
                  className="input input--mono"
                  value={to}
                  aria-label="Período — até"
                  onChange={(iso) => muda(setTo)(iso)}
                />
              </div>
            </div>
            <Field label="Número" htmlFor="nf-numero">
              <input
                id="nf-numero"
                className="input"
                value={numero}
                onChange={(event) => muda(setNumero)(event.target.value)}
                placeholder="Número, chave ou fornecedor"
              />
            </Field>
            <Field label="Fornecedor" htmlFor="nf-fornecedor">
              <select
                id="nf-fornecedor"
                className="input"
                value={personId}
                onChange={(event) => muda(setPersonId)(event.target.value)}
              >
                <option value="">Todos os fornecedores</option>
                {(suppliers.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.tradeName || s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Conciliação financeira" htmlFor="nf-financeiro">
              <select
                id="nf-financeiro"
                className="input"
                value={financeiro}
                onChange={(event) => muda(setFinanceiro)(event.target.value)}
              >
                <option value="">Todas</option>
                <option value="sim">Conciliadas</option>
                <option value="nao">Pendentes</option>
              </select>
            </Field>
            <Field label="Conciliação estoque" htmlFor="nf-estoque">
              <select
                id="nf-estoque"
                className="input"
                value={estoque}
                onChange={(event) => muda(setEstoque)(event.target.value)}
              >
                <option value="">Todas</option>
                <option value="sim">Conciliadas</option>
                <option value="nao">Pendentes</option>
              </select>
            </Field>
            <Field label="Tipo de entrada" htmlFor="nf-tipo">
              <select
                id="nf-tipo"
                className="input"
                value={kind}
                onChange={(event) => muda(setKind)(event.target.value)}
              >
                <option value="">Todas</option>
                {FISCAL_DOCUMENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {FISCAL_DOCUMENT_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {temFiltro && (
            <button type="button" className="nf-limpar" onClick={limparTudo}>
              <RotateCcw size={14} aria-hidden /> Limpar filtros
            </button>
          )}
        </section>

        <SummaryCards cards={cards} loading={summaryQuery.isPending} />

        {/* Abas por tipo, com a contagem de cada um — o mesmo filtro do
            "Tipo de entrada", à mão. Só os tipos que existem no período, e as
            contagens ignoram o próprio filtro de tipo: escolher uma aba não
            pode zerar as outras, senão some o caminho de volta. */}
        {abas.length > 1 && (
          <div className="abas" role="tablist" aria-label="Tipo de documento">
            <button
              type="button"
              role="tab"
              aria-selected={kind === ''}
              className={`aba${kind === '' ? ' is-active' : ''}`}
              onClick={() => muda(setKind)('')}
            >
              Todos <span className="aba__conta">{totalDeDocumentos}</span>
            </button>
            {abas.map((aba) => (
              <button
                key={aba.kind}
                type="button"
                role="tab"
                aria-selected={kind === aba.kind}
                className={`aba${kind === aba.kind ? ' is-active' : ''}`}
                onClick={() => muda(setKind)(aba.kind)}
              >
                {FISCAL_DOCUMENT_KIND_LABELS[aba.kind]} <span className="aba__conta">{aba.count}</span>
              </button>
            ))}
          </div>
        )}

        {listQuery.isPending ? (
          <TableSkeleton />
        ) : listQuery.isError ? (
          <ErrorState
            message={errorMessage(listQuery.error)}
            onRetry={() => void listQuery.refetch()}
          />
        ) : (
          <div className="nf-lista">
          <SmartDataTable
            caption="Documentos fiscais"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            onRowClick={(row) => void navigate({ to: documentoPath(row.id) })}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
            emptyState={
              temFiltro ? (
                <NoResultsState onClear={limparTudo} />
              ) : (
                <EmptyState
                  title="Nenhuma nota de entrada"
                  description="As NF-e chegam pela busca na SEFAZ (certificado digital) ou pelo XML. Recibo, boleto e fatura sem XML entram em Nova entrada."
                  action={
                    can('documentos-fiscais:criar') ? (
                      <Button icon={FileUp} onClick={() => setImporting(true)}>
                        Importar NF-e
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
          </div>
        )}
      </div>

      <ImportXmlModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={(id) => {
          setImporting(false);
          refresh();
          void navigate({ to: documentoPath(id) });
        }}
      />

      <DocumentoManualModal
        open={manual}
        onClose={() => setManual(false)}
        onCreated={(id) => {
          setManual(false);
          refresh();
          void navigate({ to: documentoPath(id) });
        }}
      />

      {financeiroDe && (
        <ConciliacaoFinanceiraModal
          documentId={financeiroDe.id}
          onClose={() => setFinanceiroDe(null)}
          onChanged={refresh}
        />
      )}

      {itensDe && (
        <ConciliacaoItensModal
          documentId={itensDe.id}
          onClose={() => setItensDe(null)}
          onChanged={refresh}
        />
      )}

      <ConfirmDialog
        open={Boolean(excluindo)}
        title={`Excluir a nota ${excluindo?.number ?? ''}?`}
        message="Ela sai da lista. Nota que já virou conta a pagar ou entrada de estoque não pode ser excluída."
        confirmLabel="Excluir nota"
        destructive
        loading={removeMutation.isPending}
        error={removeMutation.error ? errorMessage(removeMutation.error) : undefined}
        onClose={() => {
          setExcluindo(null);
          removeMutation.reset();
        }}
        onConfirm={() => excluindo && removeMutation.mutate(excluindo.id)}
      />
    </>
  );
}

/* ------------------------------ importar XML ------------------------------ */

export function ImportXmlModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (id: string) => void;
}) {
  const [xml, setXml] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setXml('');
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<FiscalDocument>('/fiscal-documents/import-xml', body),
    onSuccess: (created) => onImported(created.id),
  });

  // O nome do arquivo não vale nada: o que manda é o conteúdo, e é o servidor
  // que decide se é XML de verdade (e recusa DOCTYPE e arquivo gigante).
  const readFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setXml(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Importar NF-e"
      description="Escolha o arquivo XML ou cole o conteúdo — lemos emitente, itens e totais"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={xml.trim().length < 50}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ xml })}
          >
            Importar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Arquivo XML" htmlFor="doc-file">
          <input
            id="doc-file"
            className="input"
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={(event) => readFile(event.target.files?.[0])}
          />
        </Field>

        <Field label="Ou cole o XML" htmlFor="doc-xml">
          <textarea
            id="doc-xml"
            className="input input--mono"
            rows={10}
            value={xml}
            onChange={(event) => setXml(event.target.value)}
            placeholder="<nfeProc …>"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* --------------------------- documento manual ----------------------------- */

/**
 * Nova entrada à mão — o recibo do chaveiro, o boleto, a fatura, o contrato.
 *
 * NF-e, NFC-e e CT-e não aparecem nos tipos: chegam pela SEFAZ (certificado
 * digital) ou pelo XML. Cadastrar na mão seria a porta dos fundos para escapar
 * da conferência da chave — e a nota inventada entraria no estoque.
 */
function DocumentoManualModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const suppliers = useSuppliers();
  const [wasOpen, setWasOpen] = useState(open);

  const [kind, setKind] = useState('RECIBO');
  const [number, setNumber] = useState('');
  const [personId, setPersonId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO());
  const [entryDate, setEntryDate] = useState(todayISO());
  const [totalCents, setTotalCents] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setKind('RECIBO');
      setNumber('');
      setPersonId('');
      setSupplierName('');
      setIssueDate(todayISO());
      setEntryDate(todayISO());
      setTotalCents(null);
      setReason('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<FiscalDocument>('/fiscal-documents', body),
    onSuccess: (created) => onCreated(created.id),
  });
  const errors = fieldErrors(mutation.error);

  const fornecedor = (suppliers.data ?? []).find((s) => s.id === personId);
  const nome = supplierName || fornecedor?.tradeName || fornecedor?.name || '';
  const podeSalvar = Boolean(
    number.trim() && nome.trim() && (totalCents ?? 0) > 0 && reason.trim().length >= 5,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Nova entrada manual"
      description="Para o que não tem XML: recibo, boleto, fatura, contrato, comprovante."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={Save}
            disabled={!podeSalvar}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                kind,
                number: number.trim(),
                personId: personId || null,
                supplierName: nome.trim(),
                issueDate,
                entryDate: entryDate || null,
                totalCents: totalCents ?? 0,
                reason: reason.trim(),
              })
            }
          >
            Salvar entrada
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection title="Dados do documento">
        <div className="nf-manual">
          <Field label="Tipo de entrada *" htmlFor="manual-kind" error={errors.kind}>
            <select
              id="manual-kind"
              className="input"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              {FISCAL_DOCUMENT_KINDS.filter((k) => !XML_ONLY_KINDS.includes(k)).map((k) => (
                <option key={k} value={k}>
                  {FISCAL_DOCUMENT_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Número *" htmlFor="manual-number" error={errors.number}>
            <input
              id="manual-number"
              className="input"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Recibo 014"
            />
          </Field>
          <Field label="Data de emissão *" htmlFor="manual-date" error={errors.issueDate}>
            <DateInput
              id="manual-date"
              className="input input--mono"
              value={issueDate}
              onChange={(iso) => setIssueDate(iso)}
            />
          </Field>
          <Field label="Data de entrada" htmlFor="manual-entry" error={errors.entryDate}>
            <DateInput
              id="manual-entry"
              className="input input--mono"
              value={entryDate}
              onChange={(iso) => setEntryDate(iso)}
            />
          </Field>
        </div>

        <div className="nf-manual nf-manual--fornecedor">
          <Field label="Fornecedor" htmlFor="manual-person" error={errors.personId}>
            <select
              id="manual-person"
              className="input"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
            >
              <option value="">Não cadastrado — digitar ao lado</option>
              {(suppliers.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.tradeName || s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Quem emitiu *"
            htmlFor="manual-supplier"
            error={errors.supplierName}
            hint={fornecedor ? 'Vem do cadastro; troque se o papel traz outro nome.' : undefined}
          >
            <input
              id="manual-supplier"
              className="input"
              value={nome}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="José da Silva Chaveiro"
            />
          </Field>
          <Field label="Valor *" htmlFor="manual-total" error={errors.totalCents}>
            <MoneyInput id="manual-total" value={totalCents} onChange={setTotalCents} />
          </Field>
        </div>

        <Field
          label="Observações — de onde veio este documento *"
          htmlFor="manual-reason"
          error={errors.reason}
          hint="Fica na trilha: documento à mão é declaração de alguém, não dado fiscal."
        >
          <textarea
            id="manual-reason"
            className="input"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Recibo entregue na portaria pelo chaveiro que abriu o portão 3"
          />
        </Field>
        <div className="nf-contador">{reason.length}/500</div>
      </FormSection>

      <div className="nf-aviso">
        <FileText size={16} aria-hidden /> Produtos e quantidades entram com a NF-e — pela busca
        na SEFAZ ou importando o XML. A entrada manual vai para o Financeiro (conta a pagar).
      </div>
    </Modal>
  );
}
